/*
 * Part of the RubinRed CMS.
 * @author    Marco Tisi <tisi@workup.it>
 * @license   Proprietary
 * @copyright 2015-2017, Workup Srl
 * @link      http://rubinred.it
 */

import {get, set} from 'lodash';
import Axios from 'axios';
import 'es6-promise/auto';
import FontAwesome from 'js/vendor/font-awesome';
import Raven from 'raven-js';
import UIkit from 'js/vendor/uikit';
import Vue from 'js/vendor/vue';
import Vuex from 'vuex';
import ComponentHelpers from 'js/utils/ComponentHelpers';
import Qs from 'qs';
import i18n from "js/vendor/vue-i18n";

class RubinRed {
  constructor() {
    this.version = null;
    this.token = null;
    this.urls = new Map();
    this.store = null;
    this.locales = {
      current: null,
      primary: null,
      fallback: null,
      supported: {},
    };
    this.attributes = {};
    this.Utils = {
      flattenLocale: (item, locale = null) => {
        locale = this.isSupportedLocale(locale) ? locale : this.getCurrentLocale();
        if (!locale || !item[locale]) {
          return item;
        }

        item = {...item, ...item[locale]};
        delete item[locale];

        return item;
      },
    };
    this.Blueprints = {};
    this.loadVue();
    this.loadQs();
    this.loadUIkit();
    this.loadAxios();
    this.loadFontAwesome();
  }

  loadFontAwesome() {
    this.FontAwesome = FontAwesome;
    this.FontAwesome.dom.watch();
  }

  loadUIkit() {
    // noinspection JSUnresolvedVariable
    this.UIkit = UIkit;
  }

  loadQs() {
    this.Qs = Qs;
  }

  loadVue() {
    this.loadVuex();
    this.loadI18n();
    Vue.use(ComponentHelpers, {Utils: this.Utils});
    this.Vue = Vue;
    this.dispatcher = new Vue();

    return this;
  }

  loadI18n() {
    this.i18n = i18n;

    return this;
  }

  loadVuex() {
    Vue.use(Vuex);
    this.store = new Vuex.Store({});

    return this;
  }

  loadAxios() {
    this.Axios = Axios;
    this.Axios.defaults.paramsSerializer = (params) => this.Qs.stringify(params);
    this.Axios.interceptors.response.use(response => {
      let data = response.data;
      if (!this.UIkit.util.isObject(data) || !data.alerts) {
        return response;
      }

      ['primary', 'success', 'warning', 'danger'].forEach(status => {
        if (!data.alerts[status]) {
          return;
        }

        data.alerts[status].forEach(message => {
          let options = {
            status: status
          };
          if (status === 'danger' || status === 'warning') {
            options.timeout = 0;
          }
          this.UIkit.notification(message, options);
        });
      });
      delete response.data.alerts;

      return response;
    }, error => {
      this.UIkit.notification(this.i18n.t('frontend-message.something_wrong', {code: error.response.status}), {
        status: 'danger',
        timeout: 0,
      });

      return Promise.reject(error);
    });

    return this;
  }

  loadRaven(config) {
    this.Raven = Raven;

    this.Raven
      .config(config.secure_dsn, {
        release: config.release,
        tags: config.tags
      })
      .install();

    return this;
  }

  init() {
    document.querySelectorAll('[data-reactive]').forEach(element => {
      const store = this.store;
      const i18n = this.i18n;
      new Vue({
        i18n,
        store,
        el: element
      });
    });

    this.$emit('init', this);
  }

  setVersion(version) {
    this.version = version;

    return this;
  }

  getVersion() {
    return this.version;
  }

  setToken(token) {
    this.token = token;
    this.Axios.defaults.headers.common = {
      'X-CSRF-TOKEN': token,
      'X-Requested-With': 'XMLHttpRequest'
    };

    return this;
  }

  getToken() {
    return this.token;
  }

  setUrl(name, url) {
    this.urls.set(name, url);

    return this;
  }

  getUrl(name) {
    return this.urls.get(name);
  }

  setCurrentLocale(locale) {
    this.locales.current = locale;
    this.i18n.locale = locale;

    return this;
  }

  getCurrentLocale() {
    return this.locales.current;
  }

  setPrimaryLocale(locale) {
    this.locales.primary = locale;

    return this;
  }

  getPrimaryLocale() {
    return this.locales.primary;
  }

  setFallbackLocale(locale) {
    this.locales.fallback = locale;
    this.i18n.fallbackLocale = locale;

    return this;
  }

  getFallbackLocales() {
    return this.locales.fallback;
  }

  setSupportedLocales(locales) {
    this.locales.supported = locales;

    return this;
  }

  getSupportedLocale() {
    return this.locales.supported;
  }

  isSupportedLocale(locale) {
    return locale && !! this.locales.supported[locale];
  }

  set(key, value) {
    set(this.attributes, key, value);

    return this;
  }

  get(key, defaultValue) {
    return get(this.attributes, key, defaultValue);
  }

  initGridStore(params) { //TODO: move to proper file
    this.store.registerModule('grid', {
      namespaced: true,
      state: {
        page: params.page || 1,
        perPage: params.perPage || 15,
        filters: params.filters || {},
        order: params.order || null,
        direction: params.direction || null,
        meta: {}
      },
      mutations: {
        setPage(state, page) {
          state.page = parseInt(page);
        },
        nextPage(state) {
          state.page++;
        },
        setPerPage(state, perPage) {
          state.page = 1;
          state.perPage = parseInt(perPage);
        },
        addFilter(state, filter) {
          state.page = 1;
          state.filters = {...state.filters, [filter.column]: filter.value || undefined};
        },
        removeFilter(state, filter) {
          state.page = 1;
          let value = state.filters[filter.column] || [];
          if(Array.isArray(value)) {
            let pos = value.indexOf(filter.value);
            if (pos >= 0) {
              value.splice(pos, 1);
            }
          } else {
            value = undefined;
          }

          state.filters = {...state.filters, [filter.column]: value};
        },
        setOrder(state, order) {
          state.page = 1;
          state.order = order.column;
          state.direction = order.direction;
        },
        resetOrder(state) {
          state.page = 1;
          state.order = null;
        },
        setMeta(state, meta) {
          state.meta = meta;
        },
        setState(state, newState) {
          state.page = newState.page;
          state.perPage = newState.perPage;
          state.order = newState.order;
          state.direction = newState.direction;
          state.filters = newState.filters;
          state.meta = newState.meta;
        }
      }
    });
  }

  $emit() {
    this.dispatcher.$emit(...arguments);
  }

  $on() {
    this.dispatcher.$on(...arguments);
  }
}

export default RubinRed;



// WEBPACK FOOTER //
// ./public/themes/frontend/uikit3/assets/js/RubinRed.js